import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { startOfMonth, endOfDay, format } from 'date-fns'
import {
  apiAdminStagingRuns, apiAdminStagingTargets, apiAdminStagingExportRuns, ApiRequestError,
} from '@/lib/api'
import type { StagingRunSummary, StagingTarget } from '@nexbit/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import {
  AlertCircle, ChevronLeft, ChevronRight, Download, ListChecks, Search,
} from 'lucide-react'

/** Past runs, so a "we fixed it" from an agent can be checked against what changed. */

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  passed: { label: 'ผ่านทั้งหมด', cls: 'border-success/50 bg-success/10 text-success' },
  failed: { label: 'มีที่ไม่ผ่าน', cls: 'border-destructive/50 bg-destructive/10 text-destructive' },
  error: { label: 'ผิดพลาด', cls: 'border-destructive/50 bg-destructive/10 text-destructive' },
  running: { label: 'กำลังทดสอบ', cls: 'border-primary/50 bg-primary/10 text-primary' },
  aborted: { label: 'ถูกยกเลิก', cls: 'border-border bg-muted text-muted-foreground' },
}

const SUITE_TH: Record<string, string> = {
  full: 'ครบวงจร', outbound: 'กระเป๋าเงิน', inbound: 'ขาเข้า', smoke: 'เชื่อมต่อ', custom: 'เลือกเอง',
}

const toDateInput = (d: Date) => format(d, 'yyyy-MM-dd')

export default function StagingRunsPage() {
  const navigate = useNavigate()
  const [from, setFrom] = useState(toDateInput(startOfMonth(new Date())))
  const [to, setTo] = useState(toDateInput(endOfDay(new Date())))
  const [partnerId, setPartnerId] = useState('')
  const [page, setPage] = useState(1)

  const [targets, setTargets] = useState<StagingTarget[]>([])
  const [rows, setRows] = useState<StagingRunSummary[] | null>(null)
  const [total, setTotal] = useState(0)
  const [limit, setLimit] = useState(20)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const search = useCallback(async (targetPage: number) => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiAdminStagingRuns({
        partnerId: partnerId || undefined,
        from: new Date(from).toISOString(),
        to: new Date(`${to}T23:59:59`).toISOString(),
        page: targetPage,
        limit,
      })
      setRows(res.items)
      setTotal(res.total)
      setLimit(res.limit)
      setPage(targetPage)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'โหลดประวัติไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [partnerId, from, to, limit])

  useEffect(() => {
    void apiAdminStagingTargets().then(setTargets).catch(() => setTargets([]))
    void search(1)
    // Runs once: the filter form drives every search after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleExport() {
    setExporting(true)
    try {
      await apiAdminStagingExportRuns({
        from: new Date(from).toISOString(),
        to: new Date(`${to}T23:59:59`).toISOString(),
        partnerId: partnerId || undefined,
      })
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'ส่งออกไม่สำเร็จ')
    } finally {
      setExporting(false)
    }
  }

  const inputClass = cn(
    'h-9 rounded-[var(--radius-sm)] border border-input bg-background px-3 text-sm',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  )
  const totalPages = Math.max(1, Math.ceil(total / limit))

  return (
    <div className="max-w-5xl p-4 md:p-6">
      <h1 className="text-lg font-semibold tracking-tight">ประวัติผลทดสอบ</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        ทุกครั้งที่กดทดสอบจะถูกเก็บไว้ 30 วัน — ใช้เทียบว่าเอเจนต์แก้ตามที่แจ้งไปแล้วหรือยัง
      </p>

      <form
        onSubmit={(e) => { e.preventDefault(); void search(1) }}
        className="mt-5 overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]"
      >
        <div className="border-b border-border bg-secondary/40 px-4 py-2.5 text-sm font-semibold">กรอง</div>
        <div className="flex flex-wrap items-end gap-3 p-4">
          <div>
            <label htmlFor="runs-from" className="mb-1 block text-xs text-muted-foreground">ตั้งแต่</label>
            <input id="runs-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="runs-to" className="mb-1 block text-xs text-muted-foreground">ถึง</label>
            <input id="runs-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="runs-agent" className="mb-1 block text-xs text-muted-foreground">เอเจนต์</label>
            <select id="runs-agent" value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className={inputClass}>
              <option value="">ทั้งหมด</option>
              {targets.map((t) => <option key={t.partnerId} value={t.partnerId}>{t.code}</option>)}
            </select>
          </div>
          <Button type="submit" size="sm" className="gap-1.5">
            <Search className="h-3.5 w-3.5" />
            ค้นหา
          </Button>
        </div>
      </form>

      {error && (
        <div role="alert" className="mt-4 flex items-start gap-2 rounded-[var(--radius-sm)] border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <section className="mt-4 overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
        <div className="flex items-center gap-2 border-b border-border bg-secondary/40 px-4 py-3">
          <ListChecks className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">ผลลัพธ์</span>
          {rows !== null && <span className="text-xs text-muted-foreground">{total} รายการ</span>}
          {rows !== null && rows.length > 0 && (
            <Button variant="outline" size="sm" className="ml-auto gap-1.5" onClick={() => void handleExport()}>
              <Download className="h-3.5 w-3.5" />
              {exporting ? 'กำลังส่งออก...' : 'ส่งออก CSV'}
            </Button>
          )}
        </div>

        {loading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}
          </div>
        ) : rows === null || rows.length === 0 ? (
          <p className="p-8 text-center text-sm text-muted-foreground">ไม่พบผลทดสอบในช่วงที่เลือก</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>เวลา</TableHead>
                    <TableHead>เอเจนต์</TableHead>
                    <TableHead>ชุด</TableHead>
                    <TableHead>สถานะ</TableHead>
                    <TableHead className="text-right">ผ่าน / ทั้งหมด</TableHead>
                    <TableHead className="text-right">เวลาที่ใช้</TableHead>
                    <TableHead>ผู้ทดสอบ</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const badge = STATUS_BADGE[r.status] ?? STATUS_BADGE['aborted']!
                    return (
                      <TableRow
                        key={r.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/admin/staging/runs/${r.id}`)}
                      >
                        <TableCell className="font-mono text-xs">
                          {format(new Date(r.startedAt), 'dd/MM HH:mm')}
                        </TableCell>
                        <TableCell className="font-medium">{r.partnerCode}</TableCell>
                        <TableCell className="text-xs">{SUITE_TH[r.suite] ?? r.suite}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={badge.cls}>{badge.label}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {r.passedCount} / {r.totalCount}
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-muted-foreground">
                          {r.durationMs === null ? '—' : `${(r.durationMs / 1000).toFixed(1)}s`}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{r.actorUsername ?? '—'}</TableCell>
                        <TableCell><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-border px-4 py-2.5">
                <span className="text-xs text-muted-foreground">หน้า {page} จาก {totalPages}</span>
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => void search(page - 1)}>
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => void search(page + 1)}>
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}
